/* Emèlle theme — motion layer on the bundled GSAP core. Progressive: main.js keeps everything working without it.
   Drawer timeline, page exit fade, candle strip loop (also the catalogue chips on phones), the season grid's entrance and emoji swarm. Nothing runs under prefers-reduced-motion. */
(() => {
  if (!window.gsap) return;
  const EM = window.EM = window.EM || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  if (reduced) return;
  document.documentElement.classList.add('has-gsap');

  /* ---- drawer: scrim fades, panel slides, links rise ---- */
  EM.motion = EM.motion || {};
  EM.motion.drawerOpen = (panel, scrim, links) => new Promise(resolve => {
    gsap.timeline({ onComplete: resolve })
      .fromTo(scrim, { autoAlpha: 0 }, { autoAlpha: 1, duration: .3, ease: 'power1.out' }, 0)
      .fromTo(panel, { xPercent: -100 }, { xPercent: 0, duration: .45, ease: 'power3.out' }, 0)
      .fromTo(links, { y: 14, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: .4, ease: 'power2.out', stagger: .045 }, .12);
  });
  EM.motion.drawerClose = (panel, scrim, links) => new Promise(resolve => {
    gsap.timeline({ onComplete: () => { gsap.set([panel, scrim, ...links], { clearProps: 'all' }); resolve(); } })
      .to(links, { autoAlpha: 0, duration: .12 }, 0)
      .to(panel, { xPercent: -100, duration: .25, ease: 'power3.in' }, 0)
      .to(scrim, { autoAlpha: 0, duration: .2 }, .05);
  });

  /* ---- page exit: internal links fade the page out, then navigate; bfcache restores clear it ---- */
  const main = $('#main'), footer = $('.ft');
  if (main && !designMode) {
    window.addEventListener('pageshow', e => { if (e.persisted) gsap.set([main, footer].filter(Boolean), { clearProps: 'all' }); });
    document.addEventListener('click', e => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.target || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      let url; try { url = new URL(a.href, location.href); } catch { return; }
      if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return;
      if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
      e.preventDefault();
      gsap.to([main, footer].filter(Boolean), { autoAlpha: 0, y: -6, duration: .18, ease: 'power1.in', overwrite: true, onComplete: () => { location.href = url.href; } });
    });
  }

  /* ---- candle strip: seamless loop (GSAP's horizontalLoop helper), slows to a stop under the pointer or focus ---- */
  function horizontalLoop(items, config) {
    items = gsap.utils.toArray(items);
    config = config || {};
    let tl = gsap.timeline({ repeat: config.repeat, paused: config.paused, defaults: { ease: 'none' }, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100) }),
      length = items.length, startX = items[0].offsetLeft, times = [], widths = [], xPercents = [], curIndex = 0,
      pixelsPerSecond = (config.speed || 1) * 100, snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1),
      totalWidth, curX, distanceToStart, distanceToLoop, item, i;
    gsap.set(items, { xPercent: (i, el) => { const w = (widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px'))); xPercents[i] = snap((parseFloat(gsap.getProperty(el, 'x', 'px')) / w) * 100 + gsap.getProperty(el, 'xPercent')); return xPercents[i]; } });
    gsap.set(items, { x: 0 });
    totalWidth = items[length - 1].offsetLeft + (xPercents[length - 1] / 100) * widths[length - 1] - startX + items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], 'scaleX') + (parseFloat(config.paddingRight) || 0);
    for (i = 0; i < length; i++) {
      item = items[i];
      curX = (xPercents[i] / 100) * widths[i];
      distanceToStart = item.offsetLeft + curX - startX;
      distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX');
      tl.to(item, { xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100), duration: distanceToLoop / pixelsPerSecond }, 0)
        .fromTo(item, { xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100) }, { xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false }, distanceToLoop / pixelsPerSecond)
        .add('label' + i, distanceToStart / pixelsPerSecond);
      times[i] = distanceToStart / pixelsPerSecond;
    }
    function toIndex(index, vars) {
      vars = vars || {};
      (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length);
      const newIndex = gsap.utils.wrap(0, length, index);
      let time = times[newIndex];
      if (time > tl.time() !== index > curIndex) { vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) }; time += tl.duration() * (index > curIndex ? 1 : -1); }
      curIndex = newIndex;
      vars.overwrite = true;
      return gsap.to(tl, Object.assign({ time }, vars));
    }
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true);
    return tl;
  }

  const strips = new WeakMap();
  const initStrip = strip => {
    const track = $('[data-strip-track]', strip), viewport = $('[data-strip-viewport]', strip);
    if (!track || !viewport) return;
    const items = [...track.children]; // candle cards, or catalogue chips
    if (items.length < 2) return;
    const pace = Number(strip.dataset.pace) || 40; // px per second on its own, however long the row
    const fling = Number(strip.dataset.fling) || 900; // how far a flick carries on: ms of the release speed (the small chips get less)
    const prev = strips.get(strip);
    const progress = prev ? prev.tl.progress() : 0;
    if (prev) { prev.tl.kill(); prev.off(); gsap.set(items, { clearProps: 'transform' }); }
    strip.classList.add('is-looping');
    viewport.scrollLeft = 0;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const tl = horizontalLoop(items, { repeat: -1, speed: pace / 100, paddingRight: gap });
    tl.progress(progress);
    const wrapT = gsap.utils.wrap(0, tl.duration());
    let resume, drag = null, thrown = null, mouseIn = false, pressing = false, dragged = false;
    // 'auto' so a timeScale tween never kills a running toIndex tween on the same timeline
    const slow = () => gsap.to(tl, { timeScale: 0, duration: .6, overwrite: 'auto' });
    const go = () => { if (!mouseIn && !drag && !thrown) gsap.to(tl, { timeScale: 1, duration: .8, overwrite: 'auto' }); };
    const hold = () => { clearTimeout(resume); if (thrown) thrown.kill(); thrown = null; gsap.killTweensOf(tl); tl.timeScale(0); };
    const later = () => { clearTimeout(resume); resume = setTimeout(go, 1400); };
    const onFocus = e => {
      if (pressing || !viewport.contains(e.target)) return; // a press focuses the link under it: that is a drag or a tap, not a keyboard visit
      const i = items.findIndex(it => it.contains(e.target));
      viewport.scrollLeft = 0; // overflow:hidden still scrolls to a focused child; keep the loop in charge
      hold();
      if (i > -1) tl.toIndex(i, { duration: .4, ease: 'power1.inOut' });
    };
    // by hand: a sideways wheel or a drag moves the row with the hand; let go while moving and it flies on, fast, easing out.
    // It picks up its slow pace a moment after the hand stops (not while the mouse rests on it), and a drag never opens a card.
    const byHand = px => { hold(); tl.time(wrapT(tl.time() + px / pace)); later(); };
    const onWheel = e => { if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || !viewport.contains(e.target)) return; e.preventDefault(); byHand(e.deltaX); };
    const onDown = e => {
      if ((e.pointerType === 'mouse' && e.button !== 0) || !viewport.contains(e.target)) return;
      hold(); pressing = true; dragged = false;
      drag = { id: e.pointerId, x: e.clientX, t: tl.time(), moved: false, pts: [[e.timeStamp, e.clientX]] };
    };
    const onMove = e => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return;
        drag.moved = dragged = true;
        try { strip.setPointerCapture(e.pointerId); } catch {}
      }
      tl.time(wrapT(drag.t - dx / pace));
      drag.pts.push([e.timeStamp, e.clientX]); if (drag.pts.length > 8) drag.pts.shift();
    };
    const onUp = e => {
      if (!drag || e.pointerId !== drag.id) return;
      const d = drag; drag = null; pressing = false;
      if (d.moved) setTimeout(() => { dragged = false; }, 0); // after the click this release may fire
      const pts = d.pts.filter(([t]) => e.timeStamp - t < 120), a = pts[0], b = pts[pts.length - 1];
      const v = e.type === 'pointerup' && d.moved && pts.length > 1 ? (b[1] - a[1]) / Math.max(16, b[0] - a[0]) : 0; // px per ms
      if (Math.abs(v) < .2) { later(); return; }
      const p = { t: tl.time() };
      thrown = gsap.to(p, { t: p.t - (v * fling) / pace, duration: Math.min(2.2, .8 + Math.abs(v) * .45), ease: 'power3.out', onUpdate: () => tl.time(wrapT(p.t)), onComplete: () => { thrown = null; later(); } });
    };
    const onClick = e => { if (dragged) { e.preventDefault(); e.stopPropagation(); } };
    const onEnter = e => { if (e.pointerType !== 'mouse') return; mouseIn = true; if (!drag && !thrown) slow(); };
    const onLeave = e => { if (e.pointerType !== 'mouse') return; mouseIn = false; go(); };
    const handlers = [[strip, 'pointerenter', onEnter], [strip, 'pointerleave', onLeave], [strip, 'focusin', onFocus], [strip, 'focusout', go],
      [strip, 'wheel', onWheel, { passive: false }], [strip, 'pointerdown', onDown], [strip, 'pointermove', onMove],
      [window, 'pointerup', onUp], [window, 'pointercancel', onUp], [strip, 'click', onClick, true], [strip, 'dragstart', e => e.preventDefault()]];
    handlers.forEach(([el, t, h, o]) => el.addEventListener(t, h, o));
    const off = () => { handlers.forEach(([el, t, h, o]) => el.removeEventListener(t, h, o)); clearTimeout(resume); if (thrown) thrown.kill(); };
    strips.set(strip, { tl, off });
  };
  const initStrips = root => $$('[data-strip]', root).forEach(initStrip);
  initStrips(document);

  /* ---- catalogue chips: on phones the same loop, only when the row does not fit. The copies are made here, so computers and
     reduced motion never get them; every rebuild starts from the plain row and measures again ---- */
  const phone = matchMedia('(max-width: 639px)');
  const initChips = nav => {
    const track = $('[data-strip-track]', nav), viewport = $('[data-strip-viewport]', nav);
    if (!track || !viewport) return;
    const prev = strips.get(nav);
    if (prev) { prev.tl.kill(); prev.off(); strips.delete(nav); }
    $$('[data-copy]', track).forEach(el => el.remove());
    gsap.set([...track.children], { clearProps: 'transform' });
    nav.classList.remove('is-looping');
    if (!phone.matches || viewport.scrollWidth <= viewport.clientWidth + 1) return;
    [...track.children].forEach(el => {
      const copy = el.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true'); copy.setAttribute('tabindex', '-1'); copy.removeAttribute('aria-current'); copy.setAttribute('data-copy', '');
      track.appendChild(copy);
    });
    initStrip(nav);
    // a collection's page starts with its own chip near the left edge
    const lit = [...track.children].findIndex(el => el.classList.contains('is-on')), loop = strips.get(nav);
    if (lit > 0 && loop) loop.tl.time(gsap.utils.wrap(0, loop.tl.duration(), loop.tl.times[lit] - 20 / (Number(nav.dataset.pace) || 40)));
  };
  const initAllChips = root => $$('[data-chips]', root).forEach(initChips);
  // the chips' widths depend on the font: measure once it is in
  (document.fonts ? document.fonts.ready : Promise.resolve()).then(() => initAllChips(document));

  // rebuild on width changes only: phones fire resize when the URL bar shows or hides
  let rt, lastW = innerWidth;
  addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (innerWidth !== lastW) { lastW = innerWidth; initStrips(document); initAllChips(document); } }, 200); });
  document.addEventListener('shopify:section:load', e => { initStrips(e.target); initAllChips(e.target); });

  /* ---- season grid and the dogs row (data-swarm): on the way down its title, icons and cards rise in as it comes up the screen, and its
     emojis swarm the screen once its row of cards reaches the middle; coming back down later swarms again, a little, at most every
     12 s. Already on screen at load, or in the editor: left as it is. ---- */
  const R = gsap.utils.random;
  // over the whole screen, or with a host (data-swarm-in: the season band) inside that section only, clipped to it
  const swarm = (emojis, n, host) => {
    if (!emojis.length) return;
    const layer = document.createElement('div'); layer.className = host ? 'swarm swarm--in' : 'swarm'; layer.setAttribute('aria-hidden', 'true');
    (host || document.body).appendChild(layer);
    const W = host ? host.clientWidth : innerWidth, H = host ? host.clientHeight : innerHeight, pool = gsap.utils.shuffle([...emojis]);
    const tl = gsap.timeline({ onComplete: () => layer.remove() });
    for (let k = 0; k < n; k++) {
      const el = document.createElement('span'); el.className = 'swarm__item'; el.textContent = pool[k % pool.length];
      el.style.fontSize = Math.round(R(22, 50)) + 'px';
      layer.appendChild(el);
      const at = k * .045, x = R(0, W - 44);
      if (k % 3 !== 2) { // two in three float up from below the screen
        const d = R(2.4, 3.6);
        gsap.set(el, { x, y: H + 20, rotation: R(-25, 25), autoAlpha: 0 });
        tl.to(el, { y: R(-60, H * .35), x: x + R(-120, 120), rotation: `+=${R(-120, 120)}`, duration: d, ease: 'power1.out' }, at)
          .to(el, { autoAlpha: 1, duration: .25 }, at)
          .to(el, { autoAlpha: 0, duration: .7, ease: 'power1.in' }, at + d - .7);
      } else { // one in three pops up in place and drifts
        const y = R(H * .1, H * .8), d = R(1.6, 2.4);
        gsap.set(el, { x, y, scale: 0, rotation: R(-30, 30) });
        tl.to(el, { scale: 1, duration: .45, ease: 'back.out(2.4)' }, at + .2)
          .to(el, { y: y - R(60, 140), x: x + R(-50, 50), rotation: `+=${R(-60, 60)}`, duration: d, ease: 'sine.out' }, at + .2)
          .to(el, { autoAlpha: 0, scale: .6, duration: .5 }, at + .2 + d - .5);
      }
    }
  };
  const initSwarm = sec => {
    if (sec.dataset.swarmReady) return;
    sec.dataset.swarmReady = '1';
    if (designMode || sec.getBoundingClientRect().top < innerHeight * .7) return;
    const emojis = (sec.dataset.swarm || '').split(/\s+/).filter(Boolean), host = sec.hasAttribute('data-swarm-in') ? sec : null;
    const head = $$('.sec__head .h1, .sec__head .sub', sec), icons = $$('.trail__i svg, .paws svg', sec), cards = $$('.grid > *, .season-grid__soon', sec);
    const clear = 'transform,opacity,visibility';
    gsap.set(head, { autoAlpha: 0, y: 24 });
    gsap.set(icons, { autoAlpha: 0, scale: 0, y: 10 });
    gsap.set(cards, { autoAlpha: 0, y: 46, scale: .94, rotation: () => R(-5, 5), transformOrigin: '50% 100%' });
    let entered = false, last = 0;
    const enter = () => {
      entered = true;
      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .to(head, { autoAlpha: 1, y: 0, duration: .7, stagger: .1, clearProps: clear })
        .to(icons, { autoAlpha: 1, scale: 1, y: 0, duration: .55, stagger: .14, ease: 'back.out(2.6)', clearProps: clear }, '-=.35')
        .to(cards, { autoAlpha: 1, y: 0, scale: 1, rotation: 0, duration: .9, stagger: .08, ease: 'back.out(1.4)', clearProps: clear }, '-=.55');
    };
    // the cards rise in as the collection comes up the screen; the emojis wait until its row of cards is in the middle of the
    // screen (on the way down) and come back, fewer, at most every 12 s
    let dir = 1, y0 = scrollY;
    addEventListener('scroll', () => { const y = scrollY; if (y !== y0) dir = y > y0 ? 1 : -1; y0 = y; }, { passive: true });
    // whichever way it comes on screen (a jump past it and back up included), or the cards would stay hidden
    new IntersectionObserver(es => es.forEach(e => {
      if (entered || !e.isIntersecting) return;
      enter();
    }), { rootMargin: '0px 0px -30% 0px' }).observe(sec);
    new IntersectionObserver(es => es.forEach(e => {
      if (!e.isIntersecting || dir < 0) return;
      const now = performance.now();
      if (!last) { swarm(emojis, innerWidth < 700 ? 16 : 30, host); last = now; }
      else if (now - last > 12000) { swarm(emojis, innerWidth < 700 ? 8 : 12, host); last = now; }
    }), { rootMargin: '-45% 0px -45% 0px' }).observe($('.grid', sec) || sec);
  };
  $$('[data-swarm]').forEach(initSwarm);
  document.addEventListener('shopify:section:load', e => $$('[data-swarm]', e.target).forEach(initSwarm));
})();
